<script setup>
import { onMounted, provide, ref } from 'vue';
import PrintableCharacterList from '@/views/character-view-printable/sections/PrintableCharacterList.vue';
import PrintableHeaderWildsWithin from '@/views/character-view-printable/sections/PrintableHeaderWildsWithin.vue';
import PrintableSpecialAttributesSectionWildsWithin from '@/views/character-view-printable/sections/PrintableSpecialAttributesSectionWildsWithin.vue';
import { toTitleCase } from '@/utils/index.js';

const props = defineProps({
  character: {
    type: Object,
    required: true,
  },
  system: {
    type: Object,
    required: true,
  },
});

provide('printCharacter', props.character);
provide('printSystem', props.system);

const getEquipmentItems = () => {
  const items = [];
  const listDef = props.system.listDefinitions.find((l) => l.listName.toLowerCase() === 'equipment');
  if (listDef) {
    const charList = props.character.characterLists.find((l) => l.listDefinition === listDef._id);
    if (charList) {
      items.push(...charList.items);
    }
  }
  return items;
};

const getObjectParenthetical = (item) => {
  let parenthetical = item.objectDefinition.objectName;
  if (item.objectDefinition._id === '65a6bc4a2ba2f6122ce83623') {
    parenthetical = item?.fields?.weaponclassification;
  }
  return parenthetical;
};

const getCharacterAttributeRank = (attributeID, defaultValue = 0) => {
  const characterAttributes = props.character.characterAttributes || [];
  const attrib = characterAttributes.find((a) => a.systemAttribute === attributeID);
  return attrib ? attrib.rank : defaultValue;
};

const systemAttributes = ref([]);
const characterAttributes = ref([]);

const compareAttributes = (a, b) => {
  // sort first by systemAttribute.useQualifiers, then by systemAttribute.attributeName
  if (a.systemAttribute.useQualifiers > b.systemAttribute.useQualifiers) {
    return -1;
  }
  if (a.systemAttribute.useQualifiers < b.systemAttribute.useQualifiers) {
    return 1;
  }
  if (a.systemAttribute.attributeName < b.systemAttribute.attributeName) {
    return -1;
  }
  if (a.systemAttribute.attributeName > b.systemAttribute.attributeName) {
    return 1;
  }
  return 0;

  // if (a.systemAttribute.attributeName < b.systemAttribute.attributeName) {
  //   return -1;
  // }
  // if (a.systemAttribute.attributeName > b.systemAttribute.attributeName) {
  //   return 1;
  // }
  // return 0;
};

const getSortedAttributes = () => {
  const localAttributes = [];
  systemAttributes.value = [];

  props.system.paths.forEach((path) => {
    path.tiers.forEach((tier) => {
      if (!tier.hidden) {
        systemAttributes.value.push(...tier.attributes);
      }
    });
  });

  localAttributes.push(...props.character.characterAttributes.map((a) => {
    const attrib = systemAttributes.value.find((s) => s._id === a.systemAttribute);
    return { ...a, systemAttribute: attrib };
  }));

  characterAttributes.value = localAttributes.filter((a) => !!a.systemAttribute).sort(compareAttributes);
};

const makeFieldList = (objectFields, item) => {
  const fieldList = [];
  objectFields.forEach((field) => {
    if (item.fields[field.name]) {
      fieldList.push(`${toTitleCase(field.name)}: ${item.fields[field.name]}`);
    }
  });
  return fieldList;
};

const makeNoLabelFieldList = (objectFields, item) => {
  const fieldList = [];
  objectFields.forEach((field) => {
    if (item.fields[field.name]) {
      fieldList.push(item.fields[field.name]);
    }
  });
  return fieldList;
};

onMounted(() => {
  // eslint-disable-next-line vue/no-mutating-props
  props.character.equipment = getEquipmentItems();
  getSortedAttributes();
});
</script>

<template>
  <div class="char-sheet no-anything">
    <div class="page page-1 no-anything">
      <printable-header-wilds-within />
      <div class="page-body">
        <div class="w3-row stats-section">
          <div class="w3-col" style="height: 100%; padding-right: 0.1in; width: 2in;">
            <div class="w3-row" style="height: 20%;">
              <div class="w3-col w3-threequarter ability-label">
                Agility
              </div>
              <div class="w3-col w3-quarter w3-right-align ability-value">
                {{ getCharacterAttributeRank('69dd1cd2d90e4bf5c8c77504', 1) }}
              </div>
            </div>
            <div class="w3-row" style="height: 20%;">
              <div class="w3-col w3-threequarter ability-label">
                Charm
              </div>
              <div class="w3-col w3-quarter w3-right-align ability-value">
                {{ getCharacterAttributeRank('69dd1cdbd90e4bf5c8c7754b', 1) }}
              </div>
            </div>
            <div class="w3-row" style="height: 20%;">
              <div class="w3-col w3-threequarter ability-label">
                Might
              </div>
              <div class="w3-col w3-quarter w3-right-align ability-value">
                {{ getCharacterAttributeRank('69dd1cecd90e4bf5c8c77592', 1) }}
              </div>
            </div>
            <div class="w3-row" style="height: 20%;">
              <div class="w3-col w3-threequarter ability-label">
                Spirit
              </div>
              <div class="w3-col w3-quarter w3-right-align ability-value">
                {{ getCharacterAttributeRank('69dd1cf4d90e4bf5c8c775d9', 1) }}
              </div>
            </div>
            <div class="w3-row" style="height: 20%;">
              <div class="w3-col w3-threequarter ability-label">
                Wit
              </div>
              <div class="w3-col w3-quarter w3-right-align ability-value">
                {{ getCharacterAttributeRank('69dd1cffd90e4bf5c8c77624', 1) }}
              </div>
            </div>
          </div>
          <div class="w3-col" style="height: 100%; margin-left: 0.1in; width: 3.4in;">
            <div class="w3-row" style="height: calc(100% / 7);">
              <div class="w3-col w3-threequarter good-label">
                Arcana
              </div>
              <div class="w3-col w3-quarter good-value">
                {{ character.customFields['arcana'] }}
              </div>
            </div>
            <div class="w3-row" style="height: calc(100% / 7);">
              <div class="w3-col w3-threequarter good-label">
                Cloth
              </div>
              <div class="w3-col w3-quarter good-value">
                {{ character.customFields['cloth'] }}
              </div>
            </div>
            <div class="w3-row" style="height: calc(100% / 7);">
              <div class="w3-col w3-threequarter good-label">
                Food
              </div>
              <div class="w3-col w3-quarter good-value">
                {{ character.customFields['ingredients'] }}
              </div>
            </div>
            <div class="w3-row" style="height: calc(100% / 7);">
              <div class="w3-col w3-threequarter good-label">
                Metal
              </div>
              <div class="w3-col w3-quarter good-value">
                {{ character.customFields['metal'] }}
              </div>
            </div>
            <div class="w3-row" style="height: calc(100% / 7);">
              <div class="w3-col w3-threequarter good-label">
                Stone
              </div>
              <div class="w3-col w3-quarter good-value">
                {{ character.customFields['stone'] }}
              </div>
            </div>
            <div class="w3-row" style="height: calc(100% / 7);">
              <div class="w3-col w3-threequarter good-label">
                Wood
              </div>
              <div class="w3-col w3-quarter good-value">
                {{ character.customFields['wood'] }}
              </div>
            </div>
            <div class="w3-row" style="height: calc(100% / 7);">
              <div class="w3-col w3-threequarter good-label" style="font-weight: bold;">
                Influence
              </div>
              <div class="w3-col w3-quarter good-value">
                {{ character.moneyAvailable }}
              </div>
            </div>
          </div>
          <div class="w3-col character-image">
            <!-- image will go here -->
          </div>
        </div>

        <div class="w3-row">
          <div class="w3-col w3-half">
            <printable-special-attributes-section-wilds-within keyword="boon" />
          </div>
          <div class="w3-col w3-half">
            <printable-special-attributes-section-wilds-within keyword="curse" />
          </div>
        </div>

        <div class="title">
          Skills
        </div>

        <div class="w3-row">
          <div class="w3-col" style="width: 100%;">
            <div v-for="attribute in characterAttributes" :key="attribute._id">
              <div class="w3-row attribute-row">
                <div class="w3-col use-qualifiers">
                  {{ attribute.systemAttribute.useQualifiers }}&nbsp;
                </div>
                <div class="w3-col attribute-name">
                  {{ attribute.systemAttribute.attributeName }}
                </div>
                <div class="w3-col activation-type">
                  {{ attribute.systemAttribute.activationType }}
                </div>
                <div class="w3-col attribute-description">
                  {{ attribute.systemAttribute.description }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="page page-other">
        <div class="w3-row">
          <printable-character-list title="Inventory" character-list-id="68fa93fcebbdfc55cf64f69d" sort-field-name="value" sort-descending>
            <template #listItem="{ item }">
              <div class="equipment-item">
                <div class="equipment-top-subsection">
                  <div class="equipment-label">
                    {{ item.label }} <span class="parenthetical">({{ getObjectParenthetical(item) }})&nbsp;</span>
                    <span>
                      {{ makeFieldList(item.objectDefinition.fields, item).join(' / ') }}
                    </span>
                  </div>

                  <div class="equipment-description">
                    &nbsp;- {{ item.description }}
                  </div>
                </div>
                <div v-if="item.modifications && item.modifications.length" class="equipment-mods-subsection">
                  <div v-for="mod in item.modifications" :key="mod._id" class="mod">
                    <b>{{ mod.modification.modificationName }}</b>
                  </div>
                </div>
              </div>
            </template>
          </printable-character-list>
        </div>
        <div class="w3-row">
          <printable-character-list title="Companions" character-list-id="68fa9b06ebbdfc55cf656c29">
            <template #listItem="{ item }">
              <div class="equipment-item">
                <div class="equipment-top-subsection">
                  <div class="equipment-label">
                    {{ item.label }}
                    <span>
                      {{ makeNoLabelFieldList(item.objectDefinition.fields, item).join(' / ') }}
                    </span>
                  </div>

                  <div class="equipment-description">
                    &nbsp;- {{ item.description }}
                  </div>
                </div>
                <div v-if="item.modifications && item.modifications.length" class="equipment-mods-subsection">
                  <div v-for="mod in item.modifications" :key="mod._id" class="mod">
                    <b>{{ mod.modification.modificationName }}</b>
                  </div>
                </div>
              </div>
            </template>
          </printable-character-list>

          <div class="w3-row">
            <printable-character-list title="Relationships" character-list-id="68fa9fa8ebbdfc55cf661a8d">
              <template #listItem="{ item }">
                <div class="equipment-item">
                  <div class="equipment-top-subsection">
                    <div class="equipment-label">
                      {{ item.label }}
                      <span style="font-weight: normal;">
                        ({{ makeNoLabelFieldList(item.objectDefinition.fields, item).join(' / ') }})
                      </span>
                    </div>

                    <div class="equipment-description">
                      &nbsp;- {{ item.description }}
                    </div>
                  </div>
                  <div v-if="item.modifications && item.modifications.length" class="equipment-mods-subsection">
                    <div v-for="mod in item.modifications" :key="mod._id" class="mod">
                      <b>{{ mod.modification.modificationName }}</b>
                    </div>
                  </div>
                </div>
              </template>
            </printable-character-list>
          </div>

          <div class="w3-row">
            <printable-character-list title="Vehicles" character-list-id="6a1607c6a4833c9b4db04f10" :display-if-empty="false" sort-field-name="value" sort-descending>
              <template #listItem="{ item }">
                <div class="equipment-item">
                  <div class="equipment-top-subsection">
                    <div class="equipment-label">
                      {{ item.label }} <span class="parenthetical">({{ getObjectParenthetical(item) }})&nbsp;</span>
                      <span>
                        {{ makeFieldList(item.objectDefinition.fields, item).join(' / ') }}
                      </span>
                    </div>

                    <div class="equipment-description force-new-line">
                      {{ item.description }}
                    </div>
                  </div>
                  <div v-if="item.modifications && item.modifications.length" class="equipment-mods-subsection" style="margin-bottom: 0.05in;">
                    <div v-for="mod in item.modifications" :key="mod._id" class="mod" style="margin-bottom: -0.05in;">
                      <b>{{ mod.modification.modificationName }}</b>
                    </div>
                  </div>
                </div>
              </template>
            </printable-character-list>
          </div>

          <div class="w3-row">
            <printable-character-list title="Accomplishments & Notes" character-list-id="68fa9d2cebbdfc55cf65c296">
              <template #listItem="{ item }">
                <div class="equipment-item">
                  <div class="equipment-top-subsection">
                    <div class="equipment-label">
                      {{ item.label }}
                      <span style="font-weight: normal;">
                        ({{ makeNoLabelFieldList(item.objectDefinition.fields, item).join(' / ') }})
                      </span>
                    </div>

                    <div class="equipment-description">
                      &nbsp;- {{ item.description }}
                    </div>
                  </div>
                  <div v-if="item.modifications && item.modifications.length" class="equipment-mods-subsection">
                    <div v-for="mod in item.modifications" :key="mod._id" class="mod">
                      <b>{{ mod.modification.modificationName }}</b>
                    </div>
                  </div>
                </div>
              </template>
            </printable-character-list>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "@/views/character-view-printable/styles/layout.css";

.char-sheet {
  font-size: 11px;
}

.page {
  width: 8in;
  height: 10.5in;
  vertical-align: top;
}

.page-1 {
  margin: 0;
  padding: 0;
}

.page-other {
  page-break-before: always;
}

.page-1 .page-body {
  width: 100%;
  height: calc(100% - 2in);
}

/* Per-item classes */
.item-value {
  width: 0.5in;
  float: right;
  display: inline-block;
  vertical-align: top;
  font-size: 0.15in;
  overflow: hidden;
  text-overflow: ellipsis;
  border: 1px solid lightgrey;
  border-radius: 3px;
  padding: 2px;
  font-family: "Courier New", Courier, monospace;
}

.mod {
  padding-left: 20px;
  white-space: nowrap;
  display: inline-block;
  overflow: hidden;
  width: 25%;
  text-overflow: ellipsis;
}

.mod::before {
  font-size: 0.2in;
  line-height: 0.2in;
  content: "+";
  margin-right: 8px;
  color: black;
}

.equipment-item {
  width: 100%;
  display: inline-block;
  vertical-align: top;
  margin-top: -0.05in !important;
  margin-bottom: -0.1in !important;
}

.equipment-top-subsection {
  text-wrap: auto;
  margin-bottom: -0.05in;
}

.equipment-label {
  font-size: 0.8rem;
  font-weight: bold;
  display: inline-block;
}

.equipment-description {
  display: inline;
  font-size: 0.11in;
}

.force-new-line {
  display: block;
}

.equipment-mods-subsection {
  display: inline-block;
  width: 100%;
  font-size: 0.11in;
  margin-top: 0;
}

.equipment-value {
  display: inline-block;
  /*width: 1in;*/
  float: right;
  vertical-align: top;
  font-size: 0.15in;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 5px;
}

/* These are also defined as duplicates in the child PrintableCharacterList because of style scoping. */
.name-label {
  display: inline-block;
  font-size: 0.8rem;
  width: calc(100% - 0.5in);
}

.label {
  font-weight: bold;
}

.parenthetical {
  font-weight: normal !important;
}

.description {
  font-family: "Courier New", Courier, monospace;
}

.notes-section {
  width: 100%;
}

.notes-box {
  display: inline-block;
  vertical-align: top;
}

.notes-title {
  background-color: black;
  color: white;
  text-align: center;
}

.stats-section {
  padding-top: 0.1in;
  height: 2.5in;
  border-bottom: 1px solid lightgrey;
}


.ability-label {
  font-size: 0.25in;
  //font-weight: bold;
}

.ability-value {
  padding-right: 0.1in;
  font-size: 0.24in;
  font-weight: bold;
  font-family: "Courier New", Courier, monospace;
  border: 1px solid lightgrey;
}

.good-label {
  font-size: 0.18in;
  width: 1in;
}

.good-value {
  font-size: 0.18in;
  font-weight: bold;
  font-family: "Courier New", Courier, monospace;
  width: calc(100% - 1in);
  border-width: 1px;
  border-style: solid;
  border-color: lightgrey;
  padding-left: 4px;
  padding-right: 4px;
  //padding: 2px 2px 2px 5px;
}

.character-image {
  width: 2.35in;
  height: 2.35in;
  border: 1px solid lightgrey;
  border-radius: 4px;
  margin-left: 0.15in;
}

.attribute-row {
  border-bottom: 1px solid #ddd;
}

.use-qualifiers {
  width: 0.6in;
  font-size: 0.15in;
  font-weight: bold;
}

.attribute-name {
  width: 2in;
  font-size: 0.15in;
  font-weight: bold;
}

.activation-type {
  width: 0.6in;
  font-size: 0.15in;
}

.attribute-description {
  width: calc(100% - 3.2in);
  font-size: 0.15in;
  font-style: italic;
}

.title {
  background-color: black;
  color: white;
  text-align: center;
}
</style>
